import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Building2, ChevronDown, Check, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const TenantSwitcher = () => {
  const { user, activeTenantId, activeCompanyId, availableCompanies, switchTenant, switchCompany } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  if (!user) return null;

  const isSuperAdmin = user.role === 'super_admin';
  const isTenantAdmin = user.role === 'tenant_admin';
  const tenants = user.available_tenants || [];
  const companies = availableCompanies || [];

  // Only Super Admin can switch tenants and companies
  if (!isSuperAdmin) return null;
  if (tenants.length === 0 && companies.length === 0) return null;

  const activeTenant = tenants.find(t => t.id === activeTenantId);
  const activeCompany = companies.find(c => c.id === activeCompanyId);

  const tenantLabel = activeTenant ? activeTenant.name : 'Select Tenant';
  const companyLabel = activeCompany ? activeCompany.name : (companies.length > 0 ? companies[0].name : '');

  const handleSwitchTenant = async (tenantId) => {
    setSwitching(true);
    const result = await switchTenant(tenantId);
    if (result.success) {
      toast.success(result.message || 'Tenant switched');
      window.location.reload();
    } else {
      toast.error(result.error || 'Failed to switch');
    }
    setSwitching(false);
    setOpen(false);
  };

  const handleSwitchCompany = async (companyId) => {
    setSwitching(true);
    const result = await switchCompany(companyId);
    if (result.success) {
      toast.success(result.message || 'Company switched');
      window.location.reload();
    } else {
      toast.error(result.error || 'Failed to switch');
    }
    setSwitching(false);
    setOpen(false);
  };

  return (
    <div className="relative flex items-center gap-2" data-testid="tenant-switcher">
      {/* Tenant Button (Super Admin only) */}
      {isSuperAdmin && tenants.length > 0 && (
        <button
          data-testid="tenant-switcher-btn"
          onClick={() => setOpen(open === 'tenant' ? false : 'tenant')}
          disabled={switching}
          className="flex items-center gap-2 px-3 py-1.5 bg-[#0052FF]/10 hover:bg-[#0052FF]/20 border border-[#0052FF]/30 rounded-lg text-sm font-medium text-[#0052FF] transition-colors"
        >
          <Building2 className="w-4 h-4" />
          <span className="max-w-[120px] truncate">{tenantLabel}</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open === 'tenant' ? 'rotate-180' : ''}`} />
        </button>
      )}

      {/* Company Button (Super Admin + Tenant Admin when multiple companies exist) */}
      {companies.length > 1 && (
        <button
          data-testid="company-switcher-btn"
          onClick={() => setOpen(open === 'company' ? false : 'company')}
          disabled={switching}
          className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg text-sm font-medium text-emerald-700 transition-colors"
        >
          <Briefcase className="w-4 h-4" />
          <span className="max-w-[120px] truncate">{companyLabel}</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open === 'company' ? 'rotate-180' : ''}`} />
        </button>
      )}

      {/* Tenant Dropdown */}
      {open === 'tenant' && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl w-64 py-1" data-testid="tenant-switcher-dropdown">
            <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Switch Tenant</p>
            {tenants.map(t => (
              <button
                key={t.id}
                data-testid={`tenant-option-${t.code}`}
                onClick={() => handleSwitchTenant(t.id)}
                className={`w-full text-left px-3 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors ${
                  t.id === activeTenantId ? 'bg-[#0052FF]/5' : ''
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">{t.name}</p>
                  <p className="text-[10px] text-slate-400 font-mono">{t.code}</p>
                </div>
                {t.id === activeTenantId && <Check className="w-4 h-4 text-[#0052FF]" />}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Company Dropdown */}
      {open === 'company' && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl w-64 py-1" data-testid="company-switcher-dropdown">
            <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Switch Company</p>
            {companies.map(c => (
              <button
                key={c.id}
                data-testid={`company-option-${c.id}`}
                onClick={() => handleSwitchCompany(c.id)}
                className={`w-full text-left px-3 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors ${
                  c.id === activeCompanyId ? 'bg-emerald-50' : ''
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">{c.name}</p>
                  <p className="text-[10px] text-slate-400 font-mono">{c.code}</p>
                </div>
                {c.id === activeCompanyId && <Check className="w-4 h-4 text-emerald-600" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TenantSwitcher;
